import React, { useMemo, useState } from 'react';
import Heatmap, { HeatmapLegend } from './Heatmap';
import ViewToggle from './ViewToggle';
import { FadeIn } from './Loading';
import { ThemeManager, ThemeManagerToggle, ThemeSelect } from './ThemeManager';
import { compareByTheme } from './useThemes';
import { indexRowTiles } from './positionTiles';
import { useT } from '../../i18n';

// The index holdings.
//
// Same idea as the master portfolio table, minus the Platform column — every
// row here is IBKR by definition — and plus the two columns that only mean
// something inside the index: the target weight and the live market-cap rank.

// `#` is a symbol rather than a word and stays as it is in both languages.
const COLUMNS = [
    { key: 'rank', label: '#', align: 'left' },
    { key: 'symbol', labelKey: 'positions.symbol', align: 'left' },
    { key: 'name', labelKey: 'positions.name', align: 'left', wide: true },
    // The custom theme, not the growth sleeve's own `row.theme` (Robotics,
    // Space…), which is part of the index definition and not editable here.
    { key: 'customTheme', labelKey: 'themes.column', align: 'left', themed: true },
    { key: 'sector', labelKey: 'positions.sector', align: 'left' },
    { key: 'quantity', labelKey: 'positions.qty', align: 'right', numeric: true, decimals: 0 },
    { key: 'marketPrice', labelKey: 'positions.price', align: 'right', numeric: true },
    { key: 'value', labelKey: 'positions.value', align: 'right', numeric: true },
    { key: 'currentPct', labelKey: 'index.view.pctInvested', align: 'right', numeric: true, suffix: '%' },
    { key: 'targetPct', labelKey: 'index.view.target', align: 'right', numeric: true, suffix: '%' },
    { key: 'liveRank', labelKey: 'index.view.liveRank', align: 'right', numeric: true, decimals: 0 },
    { key: 'openedAt', labelKey: 'positions.opened', align: 'right' },
];

export default function IndexPositionsTable({ rows, baseCurrency, themes = null }) {
    const { t, tn, td, fmt } = useT();
    const [sleeve, setSleeve] = useState('all');
    const [managing, setManaging] = useState(false);
    const columns = themes ? COLUMNS : COLUMNS.filter((c) => !c.themed);
    const [view, setView] = useState('table');
    const [sort, setSort] = useState({ key: 'value', direction: 'desc' });

    const visible = useMemo(() => {
        const filtered = sleeve === 'all' ? rows : rows.filter((r) => r.sleeve === sleeve);
        if (sort.key === 'customTheme' && themes) {
            return [...filtered].sort(compareByTheme(themes.themeOf, sort.direction, 'value'));
        }
        const column = COLUMNS.find((c) => c.key === sort.key);
        return [...filtered].sort((a, b) => {
            const av = a[sort.key];
            const bv = b[sort.key];
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const cmp = column?.numeric
                ? Number(av) - Number(bv)
                : String(av).localeCompare(String(bv));
            return sort.direction === 'asc' ? cmp : -cmp;
        });
    }, [rows, sleeve, sort, themes]);

    const toggleSort = (key) =>
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: COLUMNS.find((c) => c.key === key)?.numeric ? 'desc' : 'asc' },
        );

    const total = visible.reduce((acc, r) => acc + Number(r.value || 0), 0);

    return (
        <>
            <div className="positions__controls">
                <div className="positions__filters">
                    {[
                        ['all', 'index.view.all'],
                        ['asset', 'index.view.assetSleeve'],
                        ['growth', 'index.view.growthEtfs'],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            className={`positions__filter${sleeve === key ? ' is-active' : ''}`}
                            onClick={() => setSleeve(key)}
                        >
                            {t(label)}
                        </button>
                    ))}
                    {themes && (
                        <ThemeManagerToggle open={managing} onToggle={() => setManaging((v) => !v)} />
                    )}
                </div>
                <div className="positions__count">
                    <ViewToggle
                        ariaLabel={t('index.view.aria')}
                        options={[['table', t('common.table')], ['heatmap', t('common.heatmap')]]}
                        value={view}
                        onChange={setView}
                    />
                    {tn('positions.count', visible.length)}
                    <span className="positions__subtotal">
                        {' · '}
                        {fmt.number(total)} {baseCurrency}
                    </span>
                </div>
            </div>

            {themes && managing && <ThemeManager themes={themes} />}
            {themes && !managing && themes.error && (
                <p className="holdings__error" role="alert">{t(themes.error)}</p>
            )}

            <FadeIn viewKey={view}>
            {view === 'heatmap' ? (
                <>
                    <Heatmap
                        height={460}
                        tiles={indexRowTiles(visible)}
                        emptyMessage={t('index.view.empty')}
                    />
                    <HeatmapLegend label={t('index.view.legend')} />
                </>
            ) : (
            <div className="positions__scroll">
                <table className="positions">
                    <thead>
                        <tr>
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={`is-${column.align}${column.wide ? ' is-wide' : ''}${
                                        sort.key === column.key ? ' is-sorted' : ''
                                    }`}
                                >
                                    <button type="button" onClick={() => toggleSort(column.key)}>
                                        {column.labelKey ? t(column.labelKey) : column.label}
                                        {sort.key === column.key && (
                                            <span aria-hidden="true">
                                                {sort.direction === 'asc' ? ' ↑' : ' ↓'}
                                            </span>
                                        )}
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={`${row.sleeve}-${row.symbol}`}>
                                {columns.map((column) => {
                                    if (column.themed) {
                                        return (
                                            <td key={column.key} className="is-left">
                                                <ThemeSelect themes={themes} symbol={row.symbol} />
                                            </td>
                                        );
                                    }
                                    const raw = row[column.key];
                                    let content;
                                    if (column.numeric) {
                                        const n = fmt.number(raw, column.decimals ?? 2);
                                        content = n === null ? null : `${n}${column.suffix ?? ''}`;
                                    } else if (column.key === 'sector') {
                                        content = td('sector', raw) || null;
                                    } else {
                                        content = raw || null;
                                    }
                                    return (
                                        <td
                                            key={column.key}
                                            className={`is-${column.align}${column.wide ? ' is-wide' : ''}`}
                                        >
                                            {content ?? <span className="positions__empty">—</span>}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}
            </FadeIn>
        </>
    );
}
